import React from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { X } from 'lucide-react';
import { DropdownFloating } from '@components/ui/dropdown-floating';
import { FloatingInput } from '@components/ui/input-floating';
import { useHealthStore } from 'src/store/health';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { HealthQuestionsSchema } from '@features/onboarding/schema/onboarding';
import { REQUIRE_DETAILS } from '@features/onboarding/components/6-health-question-form/constants';
import { FormMessageSpan } from '@components/ui/form';



const FREQUENCY_OPTIONS = [
  { value: 'Once daily', label: 'Once daily' },
  { value: 'Twice daily', label: 'Twice daily' },
  { value: 'Three times daily', label: 'Three times daily' },
  { value: 'Four times daily', label: 'Four times daily' },
  { value: 'Every X hours', label: 'Every X hours' },
  { value: 'Every other day', label: 'Every other day' },
  { value: 'Three times per week', label: 'Three times per week' },
  { value: 'Once weekly', label: 'Once weekly' },
  { value: 'Every 2 weeks', label: 'Every 2 weeks' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'One-time dose', label: 'One-time dose' },
  { value: 'As needed', label: 'As needed' },
];

interface MedicationListProps {
  fields: any[];
  control: any;
  questionId: string;
  dynamicRecipientOptions: { value: string; label: string }[];
  remove: (index: number) => void;
  append: (value: any) => void;
  onApplicantChange?: (applicant: string, index: number) => void;
}

export const MedicationList: React.FC<MedicationListProps> = ({
  fields,
  control,
  questionId,
  dynamicRecipientOptions,
  remove,
  append,
  onApplicantChange,
}) => {
  const { isFailValidation } = useHealthStore();
  
  // Watch frequency values for all medications to show custom input when needed
  const watchedFrequencies = useWatch({
    control,
    name: fields.map((_, index) => `healthQuestions.${questionId}.medications.${index}.frequency`)
  });

  return (
    <>
      {fields.map((field, index) => {
        const med = fields[index] || {};
        if (!med?.name) return null;
        const fieldName = `healthQuestions.${questionId}.medications.${index}`;
        return (
          <div className="mt-4 p-4 rounded-lg bg-white card-shadow" key={`med_${index}`}>
            <div className="flex justify-between items-start">
              <div>
                <p className="font-semibold flex flex-wrap">
                  {med?.name
                    ? med.name.split('/').map((part: string, index: number, arr: string[]) => (
                        <span key={index}>
                          {part}
                          {index < arr.length - 1 ? '/' : ''}
                        </span>
                      ))
                    : 'Unknown Medication'}
                </p>
                <p className="text-sm text-gray-500 !font-secondary">{med?.form || 'Unknown Form'}</p>
              </div>
              <button
                className="cursor-pointer"
                onClick={() => {
                  remove(index);
                }}
              >
                <X size={20} className="text-primary" />
              </button>
            </div>
            <div className="mt-4 grid grid-cols-1 max-lg:gap-2 sm:grid-cols-3 gap-4">
              <div>
                <Controller
                  control={control}
                  name={`${fieldName}.dosage`}
                  render={({ field }) => (
                    <>
                      <FloatingInput
                        id={`dosage-${index}`}
                        label="Dosage (mg)"
                        type="number"
                        step="0.01"
                        min="0.01"
                        max="9000"
                        placeholder="Enter dosage in mg"
                        inputClass="mb-auto min-h-[52px]"
                        value={field.value || ''}
                        onChange={field.onChange}
                      />
                      {isFailValidation && !field.value && (
                        <FormMessageSpan
                          className="mt-1 text-sm"
                          text={'Dosage cannot be blank.'}
                        />
                      )}
                    </>
                  )}
                />
              </div>
              <div>
                <Controller
                  control={control}
                  name={`${fieldName}.frequency`}
                  render={({ field, fieldState }) => (
                    <>
                      <DropdownFloating
                        id={`frequency-${index}`}
                        options={FREQUENCY_OPTIONS}
                        value={field.value || ''}
                        label="Frequency"
                        inputClass="mb-auto min-h-[52px]"
                        error={isFailValidation && !field.value}
                        onChange={field.onChange}
                      />
                      {isFailValidation && !field.value && (
                        <FormMessageSpan
                          className="mt-1 text-sm"
                          text={'Frequency cannot be blank.'}
                        />
                      )}
                    </>
                  )}
                />
              </div>
              <div>
                <Controller
                  control={control}
                  name={`${fieldName}.applicant`}
                  render={({ field, fieldState }) => (
                    <>
                      <DropdownFloating
                        id={`applicant-${index}`}
                        options={dynamicRecipientOptions}
                        value={field.value != null && field.value !== '' ? String(field.value) : ''}
                        label="Who takes this?"
                        inputClass="mb-auto min-h-[52px]"
                        onChange={field.onChange}
                        disabled={dynamicRecipientOptions.length <= 1}
                      />
                      {isFailValidation && !field.value && (
                        <FormMessageSpan
                          className="mt-1 text-sm"
                          text={'Recipient cannot be blank.'}
                        />
                      )}
                    </>
                  )}
                />
              </div>
            </div>
            {/* Show custom frequency input if "Every X hours" is selected */}
            {watchedFrequencies?.[index] === 'Every X hours' && (
              <div className="mt-4">
                <Controller
                  control={control}
                  name={`${fieldName}.frequencyDescription`}
                  render={({ field }) => (
                    <>
                      <FloatingInput
                        id={`custom-frequency-${index}`}
                        label="Describe frequency (e.g., Every 6 hours)"
                        type="text"
                        placeholder="Enter custom frequency"
                        inputClass="mb-auto min-h-[52px]"
                        value={field.value || ''}
                        onChange={field.onChange}
                      />
                      {isFailValidation && !field.value && (
                        <FormMessageSpan
                          className="mt-1 text-sm"
                          text={'Custom frequency description is required.'}
                        />
                      )}
                    </>
                  )}
                />
              </div>
            )}
            <Controller
              control={control}
              name={`${fieldName}.description`}
              render={({ field }) => (
                <>
                  <FloatingInput
                    id={`description-${index}`}
                    label="Can you tell us a bit about why this medication is being used?"
                    as="textarea"
                    rows={3}
                    inputClass="w-full mt-4 border rounded-[6px] p-2 text-sm !font-secondary min-h-60 max-xs:!pt-9"
                    value={field.value || ''}
                    onChange={field.onChange}
                  />
                  {REQUIRE_DETAILS && isFailValidation && !field.value && (
                    <FormMessageSpan
                      className="mt-1 text-sm"
                      text={'Reason cannot be blank.'}
                    />
                  )}
                </>
              )}
            />
          </div>
        );
      })}
    </>
  );
};
